import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Table, Select } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons';

import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import BorderlessButton from '../../../common/buttons/BorderlessButton';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';

import EclipsePayCodeMappingDrawer from './EclipsePayCodeMappingDrawer';
import EclipseDistributionOverride from './EclipseDistributionOverride';
import EclipseCustomMappingModal from './EclipseCustomMappingModal';

import colors from '../../../constants/Colors';

import {
  ontraccrTimeFields,
  eclipseTimeFields,
  CUSTOM_MAPPINGS_KEY,
  CUSTOM_MAPPING_FIELD_TYPES,
} from './eclipse.constants';

const PAY_CODE_MODE = 'payCode';
const DISTRIBUTION_MODE = 'distributionOverride';
const DRAWER_MODES = {
  pay_code: PAY_CODE_MODE,
  materialUsageDistributionType: DISTRIBUTION_MODE,
  materialTransferDistributionType: DISTRIBUTION_MODE,
};

function EclipseFieldMapping({
  value: mappings,
  onChange: onMappingsChanged,
  divisionId,
}) {
  const customFields = useSelector((state) => state.timecards.customFields);

  const [drawerMode, setDrawerMode] = useState();
  const [customMappingModalVisible, setCustomMappingModalVisible] = useState(false);
  const [editingMapping, setEditingMapping] = useState();

  const closeDrawer = useCallback(() => setDrawerMode(), []);

  const closeCustomMappingModal = useCallback(() => {
    setCustomMappingModalVisible(false);
    setEditingMapping();
  }, []);

  const openAddCustomMapping = useCallback(() => {
    setEditingMapping();
    setCustomMappingModalVisible(true);
  }, []);

  const openEditCustomMapping = useCallback((record) => {
    setEditingMapping({
      label: record.label,
      value: record.value,
    });
    setCustomMappingModalVisible(true);
  }, []);

  const onSaveMappings = useCallback((payCodeMappings) => {
    onMappingsChanged({
      ...mappings,
      payCodeMappings,
    });
    setDrawerMode();
  }, [mappings, onMappingsChanged]);

  const onSaveOverrides = useCallback((newOverride) => {
    onMappingsChanged({
      ...mappings,
      overrides: {
        ...mappings?.overrides ?? {},
        [drawerMode]: newOverride,
      },
    });
    setDrawerMode();
  }, [mappings, onMappingsChanged, drawerMode]);

  const timecardFields = useMemo(() => {
    const {
      [divisionId]: {
        fields: sections = [],
      } = {},
    } = customFields;

    // Gross deep copy
    const fieldMap = JSON.parse(JSON.stringify(ontraccrTimeFields));
    const addToMap = (type, option) => {
      if (!(type in fieldMap)) {
        fieldMap[type] = [];
      }
      fieldMap[type].push(option);
    };
    sections.forEach((section) => {
      const { fields = [], name: sectionName, hidden } = section;
      if (hidden) return;
      fields.forEach((field) => {
        const {
          id: fieldId,
          selectedType,
          configProps: {
            title,
            dataType,
          } = {},
        } = field;
        const isDropdown = selectedType === 'dropdown';
        const fullType = selectedType === 'table' || isDropdown
          ? `${selectedType} - ${dataType}`
          : selectedType;
        const option = {
          label: `${sectionName} - ${title}`,
          value: fieldId,
        };
        addToMap(fullType, option);
        // Support generic dropdown mapping
        if (isDropdown) {
          addToMap(selectedType, option);
        }
      });
    });
    return fieldMap;
  }, [ontraccrTimeFields, customFields, divisionId]);

  const customMappings = mappings?.[CUSTOM_MAPPINGS_KEY] ?? [];

  const tableData = useMemo(() => (
    eclipseTimeFields.concat(customMappings.map((field) => ({
      ...field,
      isCustom: true,
      types: CUSTOM_MAPPING_FIELD_TYPES,
    })))
  ), [customMappings]);

  const onSaveCustomMapping = useCallback(({ label, value }) => {
    if (!onMappingsChanged) return;
    const newMaps = {
      ...mappings,
    };
    if (editingMapping) {
      const { value: oldValue } = editingMapping;
      if (oldValue !== value) {
        if (newMaps[oldValue] !== undefined) {
          newMaps[value] = newMaps[oldValue];
        }
        delete newMaps[oldValue];
      }
      newMaps[CUSTOM_MAPPINGS_KEY] = customMappings.map((field) => (
        field.value === oldValue
          ? { label, value }
          : field
      ));
    } else {
      newMaps[CUSTOM_MAPPINGS_KEY] = customMappings.concat([{ label, value }]);
    }
    onMappingsChanged(newMaps);
    closeCustomMappingModal();
  }, [
    mappings,
    customMappings,
    editingMapping,
    onMappingsChanged,
    closeCustomMappingModal,
  ]);

  const onDeleteCustomMapping = useCallback((fieldValue) => {
    const newMaps = {
      ...mappings,
    };
    delete newMaps[fieldValue];
    newMaps[CUSTOM_MAPPINGS_KEY] = customMappings.filter(
      (field) => field.value !== fieldValue,
    );
    onMappingsChanged(newMaps);
  }, [mappings, customMappings, onMappingsChanged]);

  const columns = useMemo(() => [
    {
      dataIndex: 'sourceField',
      title: 'Ontraccr Field',
      width: 300,
      render: (_, record) => {
        const { type: fieldType, types: fieldTypes, value } = record;

        const {
          [value]: mappingValue,
        } = mappings ?? {};
        const {
          [fieldType]: fieldOpts = [],
        } = timecardFields;

        let allOpts = fieldOpts;
        if (fieldTypes) {
          fieldTypes.forEach((type) => {
            const {
              [type]: typeFieldOpts = [],
            } = timecardFields;
            allOpts = allOpts.concat(typeFieldOpts);
          });
        }

        if (fieldType === 'input') {
          return (
            <OnTraccrTextInput
              value={mappingValue}
              allowClear
              onChange={(e) => {
                const newMaps = {
                  ...mappings,
                };
                const fieldKey = e?.target?.value;
                if (fieldKey) {
                  newMaps[value] = fieldKey;
                } else {
                  delete newMaps[value];
                }
                onMappingsChanged(newMaps);
              }}
            />
          );
        }

        return (
          <Select
            options={allOpts}
            value={mappingValue}
            showSearch
            optionFilterProp="title"
            allowClear={!record.required}
            onChange={(fieldKey) => {
              if (!onMappingsChanged) return;
              const newMaps = {
                ...mappings,
              };
              if (fieldKey) {
                newMaps[value] = fieldKey;
              } else {
                delete newMaps[value];
              }
              onMappingsChanged(newMaps);
            }}
          />
        );
      },
    }, {
      dataIndex: 'targetField',
      title: 'Eclipse Field',
      width: 300,
      render: (_, record) => {
        const isPaycode = record.value === 'pay_code';
        const isDistroType = record.value === 'materialUsageDistributionType'
          || record.value === 'materialTransferDistributionType';
        const className = record.required
          ? 'form-required-field'
          : null;
        return (
          <span className={className}>
            {record.label}
            {
              (isPaycode || isDistroType)
              && (
                <BorderlessButton
                  iconNode={<SettingOutlined style={{ marginLeft: 0 }} />}
                  style={{ width: 20 }}
                  onClick={() => setDrawerMode(record.value)}
                />
              )
            }
            {
              record.isCustom
              && (
                <>
                  <BorderlessButton
                    iconNode={<EditOutlined style={{ marginLeft: 0 }} />}
                    style={{ width: 20 }}
                    onClick={() => openEditCustomMapping(record)}
                  />
                  <BorderlessButton
                    iconNode={(
                      <DeleteOutlined
                        style={{ color: colors.ONTRACCR_RED, marginLeft: 0 }}
                      />
                    )}
                    style={{ width: 20 }}
                    onClick={() => onDeleteCustomMapping(record.value)}
                  />
                </>
              )
            }
          </span>
        );
      },
    },
  ], [
    mappings,
    onMappingsChanged,
    timecardFields,
    onDeleteCustomMapping,
    openEditCustomMapping,
  ]);

  return (
    <>
      <div style={{ overflow: 'hidden', marginBottom: 8 }}>
        <OnTraccrButton
          title="Add Custom Mapping"
          icon={<PlusOutlined />}
          iconLeft
          onClick={openAddCustomMapping}
          style={{
            float: 'right',
            position: 'absolute',
            right: 0,
            top: -32,
            zIndex: 1000,
          }}
        />
      </div>
      <Table
        dataSource={tableData}
        columns={columns}
        pagination={false}
        size="small"
        rowKey="value"
      />
      <EclipseCustomMappingModal
        visible={customMappingModalVisible}
        onClose={closeCustomMappingModal}
        onSave={onSaveCustomMapping}
        existingMappings={customMappings}
        initialMapping={editingMapping}
      />
      <EclipsePayCodeMappingDrawer
        visible={DRAWER_MODES[drawerMode] === PAY_CODE_MODE}
        payCodeMappings={mappings?.payCodeMappings}
        onClose={closeDrawer}
        onSaveMappings={onSaveMappings}
      />
      <EclipseDistributionOverride
        visible={DRAWER_MODES[drawerMode] === DISTRIBUTION_MODE}
        override={mappings?.overrides?.[drawerMode]}
        onClose={closeDrawer}
        onSaveOverride={onSaveOverrides}
      />
    </>
  );
}

EclipseFieldMapping.propTypes = {
  value: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onChange: PropTypes.func,
  divisionId: PropTypes.string,
};

EclipseFieldMapping.defaultProps = {
  value: {},
  onChange: null,
  divisionId: null,
};

export default EclipseFieldMapping;
